/**
 * /finance/settings — finance administrators maintain the master data the expense workflow runs on.
 * Every save is an RPC that re-checks finance_admin and lands in finance_config_audit.
 */
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useOutletContext } from "react-router-dom";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useDebounce } from "../../hooks/useDebounce";
import { preventEnterSubmit } from "../../utils/preventEnterSubmit";
import Button from "../../components/common/Button";
import {
  financeErrorMessage,
  getFinanceLookups,
  importPayees,
  listApproverManagers,
  listCapabilityGrants,
  listGlMappings,
  listPayeesPage,
  PAYEE_ADMIN_PAGE_SIZE,
  listSettings,
  listStaffDirectory,
  parseCsv,
  personName,
  setApproverManager,
  setCapability,
  setSetting,
  setDepartmentAlternate,
  setTermEscalation,
  setExpenseTypeInvoiceType,
  listStateEntities,
  upsertStateEntity,
  listExportTemplates,
  saveExportTemplate,
  upsertBillingCategory,
  upsertDepartment,
  upsertExpenseType,
  upsertGlMapping,
  upsertPayee,
  upsertPaymentTerm,
} from "../../services/finance.service";
import {
  formatBcDocumentNo,
  validateBcPad,
  validateBcPrefix,
} from "./bcDocumentNo";
import type {
  FinanceAccess,
  Payee,
  PaymentReferenceBasis,
  StaffPerson,
} from "../../types/finance.types";

const inputCls = "rounded-md border border-gray-300 px-2 py-1.5 text-sm";
const SECTIONS = [
  ["departments", "Departments & approvers"],
  ["gl", "Billing categories & GL"],
  ["payees", "Payees"],
  ["types", "Expense types"],
  ["terms", "Payment terms"],
  ["managers", "Approver managers"],
  ["approvers", "Alternates & escalation"],
  ["bc", "Business Central export"],
  ["access", "Access profiles"],
  ["settings", "Rules"],
] as const;
type SectionKey = (typeof SECTIONS)[number][0];

function useSaver() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async (fn: () => Promise<unknown>, after?: () => void) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await qc.invalidateQueries({
        predicate: (q) => String(q.queryKey[0]).startsWith("finance-"),
      });
      after?.();
    } catch (e) {
      setError(financeErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return { error, busy, save, setError };
}

function Err({ msg }: { msg: string | null }) {
  return msg ? (
    <p role="alert" className="text-sm text-red-700">
      {msg}
    </p>
  ) : null;
}

function Section({
  title,
  help,
  children,
}: {
  title: string;
  help: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-500">{help}</p>
      </div>
      {children}
    </section>
  );
}

function PersonSelect({
  people,
  value,
  onChange,
  id,
  placeholder = "Choose…",
}: {
  people: StaffPerson[];
  value: string;
  onChange: (v: string) => void;
  id?: string;
  placeholder?: string;
}) {
  return (
    <select
      id={id}
      className={inputCls}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{placeholder}</option>
      {people
        .filter((p) => p.can_use_finance)
        .map((p) => (
          <option key={p.id} value={p.id}>
            {p.full_name || p.email} ({p.role})
          </option>
        ))}
    </select>
  );
}

const Active = ({ on }: { on: boolean }) => (
  <span className={on ? "text-green-700" : "text-gray-400"}>
    {on ? "Active" : "Inactive"}
  </span>
);

export default function FinanceSettings() {
  const access = useOutletContext<FinanceAccess>();
  const [section, setSection] = useState<SectionKey>("departments");
  const lookupsQ = useQuery({
    queryKey: ["finance-lookups"],
    queryFn: getFinanceLookups,
  });
  const peopleQ = useQuery({
    queryKey: ["finance-staff"],
    queryFn: listStaffDirectory,
    staleTime: 300_000,
  });
  const people = useMemo(() => peopleQ.data ?? [], [peopleQ.data]);
  const peopleMap = useMemo(
    () => new Map(people.map((p) => [p.id, p])),
    [people],
  );

  if (!access.is_finance_admin) {
    return (
      <p className="text-sm text-gray-600">
        Finance settings are for finance administrators.
      </p>
    );
  }
  const lk = lookupsQ.data;

  return (
    <div
      className="grid grid-cols-1 gap-6 lg:grid-cols-[14rem_1fr]"
      data-testid="finance-settings-page"
    >
      <nav
        className="flex flex-row flex-wrap gap-1 lg:flex-col"
        aria-label="Finance settings sections"
      >
        {SECTIONS.map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setSection(k)}
            className={`rounded-md px-3 py-2 text-left text-sm ${section === k ? "bg-primary-50 font-medium text-primary-700" : "text-gray-600 hover:bg-gray-50"}`}
          >
            {label}
          </button>
        ))}
      </nav>
      <div className="space-y-6">
        {!lk ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <>
            {section === "departments" && (
              <DepartmentsSection
                lk={lk}
                people={people}
                peopleMap={peopleMap}
              />
            )}
            {section === "gl" && <GlSection lk={lk} />}
            {section === "payees" && <PayeesSection lk={lk} />}
            {section === "types" && <TypesSection lk={lk} />}
            {section === "terms" && <TermsSection lk={lk} />}
            {section === "managers" && (
              <ManagersSection people={people} peopleMap={peopleMap} />
            )}
            {section === "approvers" && (
              <ApproversEscalationSection lk={lk} people={people} />
            )}
            {section === "bc" && <BcExportSection lk={lk} />}
            {section === "access" && <AccessSection />}
            {section === "settings" && <RulesSection />}
          </>
        )}
      </div>
    </div>
  );
}

type Lk = NonNullable<Awaited<ReturnType<typeof getFinanceLookups>>>;

function DepartmentsSection({
  lk,
  people,
  peopleMap,
}: {
  lk: Lk;
  people: StaffPerson[];
  peopleMap: Map<string, StaffPerson>;
}) {
  const { error, busy, save } = useSaver();
  const blank = {
    id: "",
    code: "",
    name: "",
    approver: "",
    bc: "",
    active: true,
  };
  const [f, setF] = useState(blank);
  return (
    <Section
      title="Departments & approvers"
      help="Each department's approver receives that department's expenses for approval. An expense cannot be submitted until its department has an approver."
    >
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500">
            <th className="py-1">Code</th>
            <th>Name</th>
            <th>Approver</th>
            <th>BC dimension</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody className="divide-y">
          {lk.departments.map((d) => (
            <tr key={d.id}>
              <td className="py-1.5 font-mono">{d.code}</td>
              <td>{d.name}</td>
              <td>
                {d.default_approver_profile_id ? (
                  personName(peopleMap, d.default_approver_profile_id)
                ) : (
                  <span className="text-amber-700">Not set</span>
                )}
              </td>
              <td className="font-mono">{d.bc_dimension_code ?? "—"}</td>
              <td>
                <Active on={d.is_active} />
              </td>
              <td className="text-right">
                <button
                  type="button"
                  className="text-primary-600 hover:underline"
                  onClick={() =>
                    setF({
                      id: d.id,
                      code: d.code,
                      name: d.name,
                      approver: d.default_approver_profile_id ?? "",
                      bc: d.bc_dimension_code ?? "",
                      active: d.is_active,
                    })
                  }
                >
                  Edit
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex flex-wrap items-end gap-2 border-t pt-3">
        <input
          aria-label="Department code"
          placeholder="Code"
          className={`${inputCls} w-28`}
          value={f.code}
          onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })}
        />
        <input
          aria-label="Department name"
          placeholder="Name"
          className={inputCls}
          value={f.name}
          onChange={(e) => setF({ ...f, name: e.target.value })}
        />
        <PersonSelect
          people={people}
          value={f.approver}
          onChange={(v) => setF({ ...f, approver: v })}
          placeholder="Approver…"
        />
        <input
          aria-label="BC dimension code"
          placeholder="BC dimension"
          className={`${inputCls} w-32`}
          value={f.bc}
          onChange={(e) => setF({ ...f, bc: e.target.value })}
        />
        <label className="flex items-center gap-1 text-sm">
          <input
            type="checkbox"
            checked={f.active}
            onChange={(e) => setF({ ...f, active: e.target.checked })}
          />
          Active
        </label>
        <Button
          size="sm"
          disabled={busy || !f.code || !f.name}
          onClick={() =>
            save(
              () =>
                upsertDepartment({
                  id: f.id || undefined,
                  code: f.code,
                  name: f.name,
                  default_approver_profile_id: f.approver || null,
                  bc_dimension_code: f.bc || null,
                  is_active: f.active,
                }),
              () => setF(blank),
            )
          }
        >
          {f.id ? "Save department" : "Add department"}
        </Button>
        {f.id && (
          <Button size="sm" variant="secondary" onClick={() => setF(blank)}>
            New
          </Button>
        )}
      </div>
      <Err msg={error} />
    </Section>
  );
}

function GlSection({ lk }: { lk: Lk }) {
  const cat = useSaver();
  const gl = useSaver();
  const mappingsQ = useQuery({
    queryKey: ["finance-gl"],
    queryFn: listGlMappings,
  });
  const blankC = { id: "", code: "", name: "", active: true };
  const [c, setC] = useState(blankC);
  const blankM = { id: "", dept: "", cat: "", type: "", gl: "", active: true };
  const [m, setM] = useState(blankM);
  const nm = (
    list: { id?: string; code?: string; name: string }[],
    v: string | null,
  ) =>
    v ? (list.find((i) => i.id === v || i.code === v)?.name ?? v) : "Any type";
  return (
    <>
      <Section
        title="Billing categories"
        help="What the money is for, as Finance groups it. Combined with the department it decides the GL account."
      >
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1">Code</th>
              <th>Name</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y">
            {lk.categories.map((x) => (
              <tr key={x.id}>
                <td className="py-1.5 font-mono">{x.code}</td>
                <td>{x.name}</td>
                <td>
                  <Active on={x.is_active} />
                </td>
                <td className="text-right">
                  <button
                    type="button"
                    className="text-primary-600 hover:underline"
                    onClick={() =>
                      setC({
                        id: x.id,
                        code: x.code,
                        name: x.name,
                        active: x.is_active,
                      })
                    }
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex flex-wrap items-end gap-2 border-t pt-3">
          <input
            aria-label="Category code"
            placeholder="Code"
            className={`${inputCls} w-36`}
            value={c.code}
            onChange={(e) => setC({ ...c, code: e.target.value.toUpperCase() })}
          />
          <input
            aria-label="Category name"
            placeholder="Name"
            className={inputCls}
            value={c.name}
            onChange={(e) => setC({ ...c, name: e.target.value })}
          />
          <label className="flex items-center gap-1 text-sm">
            <input
              type="checkbox"
              checked={c.active}
              onChange={(e) => setC({ ...c, active: e.target.checked })}
            />
            Active
          </label>
          <Button
            size="sm"
            disabled={cat.busy || !c.code || !c.name}
            onClick={() =>
              cat.save(
                () =>
                  upsertBillingCategory({
                    id: c.id || undefined,
                    code: c.code,
                    name: c.name,
                    is_active: c.active,
                  }),
                () => setC(blankC),
              )
            }
          >
            {c.id ? "Save category" : "Add category"}
          </Button>
        </div>
        <Err msg={cat.error} />
      </Section>
      <Section
        title="GL mappings"
        help="Department × billing category (optionally a specific expense type) → GL account. A type-specific row wins over an any-type row."
      >
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1">Department</th>
              <th>Category</th>
              <th>Expense type</th>
              <th>GL account</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y">
            {(mappingsQ.data ?? []).map((x) => (
              <tr key={x.id}>
                <td className="py-1.5">
                  {nm(lk.departments, x.department_id)}
                </td>
                <td>{nm(lk.categories, x.billing_category_id)}</td>
                <td>{nm(lk.types, x.expense_type_code)}</td>
                <td className="font-mono">{x.gl_account_no}</td>
                <td>
                  <Active on={x.is_active} />
                </td>
                <td className="text-right">
                  <button
                    type="button"
                    className="text-primary-600 hover:underline"
                    onClick={() =>
                      setM({
                        id: x.id,
                        dept: x.department_id,
                        cat: x.billing_category_id,
                        type: x.expense_type_code ?? "",
                        gl: x.gl_account_no,
                        active: x.is_active,
                      })
                    }
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex flex-wrap items-end gap-2 border-t pt-3">
          <select
            aria-label="Mapping department"
            className={inputCls}
            value={m.dept}
            onChange={(e) => setM({ ...m, dept: e.target.value })}
          >
            <option value="">Department…</option>
            {lk.departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          <select
            aria-label="Mapping category"
            className={inputCls}
            value={m.cat}
            onChange={(e) => setM({ ...m, cat: e.target.value })}
          >
            <option value="">Category…</option>
            {lk.categories.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          <select
            aria-label="Mapping expense type"
            className={inputCls}
            value={m.type}
            onChange={(e) => setM({ ...m, type: e.target.value })}
          >
            <option value="">Any type</option>
            {lk.types.map((d) => (
              <option key={d.code} value={d.code}>
                {d.name}
              </option>
            ))}
          </select>
          <input
            aria-label="GL account"
            placeholder="GL account"
            className={`${inputCls} w-32`}
            value={m.gl}
            onChange={(e) => setM({ ...m, gl: e.target.value })}
          />
          <label className="flex items-center gap-1 text-sm">
            <input
              type="checkbox"
              checked={m.active}
              onChange={(e) => setM({ ...m, active: e.target.checked })}
            />
            Active
          </label>
          <Button
            size="sm"
            disabled={gl.busy || !m.dept || !m.cat || !m.gl}
            onClick={() =>
              gl.save(
                () =>
                  upsertGlMapping({
                    id: m.id || undefined,
                    department_id: m.dept,
                    billing_category_id: m.cat,
                    expense_type_code: m.type || null,
                    gl_account_no: m.gl,
                    is_active: m.active,
                  }),
                () => setM(blankM),
              )
            }
          >
            {m.id ? "Save mapping" : "Add mapping"}
          </Button>
        </div>
        <Err msg={gl.error} />
      </Section>
    </>
  );
}

export function PayeesSection({ lk }: { lk: Lk }) {
  const { error, busy, save, setError } = useSaver();
  const blank: Partial<Payee> & {
    payee_type: Payee["payee_type"];
    display_name: string;
  } = {
    payee_type: "vendor",
    display_name: "",
    is_active: true,
  };
  const [f, setF] = useState(blank);
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(0);
  const [preview, setPreview] = useState<Record<string, string>[] | null>(null);
  const [result, setResult] = useState<string | null>(null);
  // Server-side search + paging: ~9,800 vendors, PostgREST returns at most 1,000 rows per request.
  // The search box is never replaced by a loading state (it keeps focus while the list refreshes).
  const term = useDebounce(filter.trim(), 300);
  useEffect(() => setPage(0), [term]);
  const listQ = useQuery({
    queryKey: ["finance-payee-admin-list", term, page],
    queryFn: () => listPayeesPage(term, page),
    placeholderData: keepPreviousData,
  });
  const shown = listQ.data?.rows ?? [];
  const total = listQ.data?.total ?? 0;
  const lastPage = Math.max(0, Math.ceil(total / PAYEE_ADMIN_PAGE_SIZE) - 1);
  const listStatus = () => {
    if (listQ.isError) {
      return "Could not load payees, please try again.";
    }
    if (listQ.isLoading) {
      return "Loading payees…";
    }
    if (total === 0) {
      return term ? "No payees match." : "No payees yet.";
    }
    const first = page * PAYEE_ADMIN_PAGE_SIZE + 1;
    return `${first}–${first + shown.length - 1} of ${total.toLocaleString()}${term ? " matching" : ""}`;
  };

  const onCsv = async (file: File | undefined) => {
    setResult(null);
    setError(null);
    if (!file) {
      return;
    }
    const rows = parseCsv(await file.text()).map((r) => ({
      payee_type: r.payee_type || r.type || "vendor",
      display_name: r.display_name || r.name || "",
      bc_vendor_no:
        r.bc_vendor_no || r.vendor_no || r.vendor_number || r.no || "",
      department_code: r.department_code || r.department || "",
      payment_terms_code: r.payment_terms_code || r.payment_terms || "",
      state: r.state || "",
      email: r.email || "",
    }));
    setPreview(rows);
  };

  return (
    <Section
      title="Payees"
      help="The vendors expenses are paid to (employee expenses go through Concur, not here). Import the Business Central vendor list as CSV (columns: name, bc_vendor_no, department_code, payment_terms_code, state, email); rows with a vendor number update the existing vendor."
    >
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          aria-label="Search payees"
          placeholder="Search by name or vendor number"
          className={`${inputCls} w-72`}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          onKeyDown={preventEnterSubmit}
          data-testid="finance-payee-search"
        />
        <label className="text-sm text-gray-700">
          Import CSV:{" "}
          <input
            type="file"
            accept=".csv,text/csv"
            className="text-sm"
            data-testid="finance-payee-csv"
            onChange={(e) => onCsv(e.target.files?.[0])}
          />
        </label>
      </div>
      {preview && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm">
          {preview.length} rows ready. The whole file is imported or none of it.{" "}
          <Button
            size="sm"
            disabled={busy || preview.length === 0}
            onClick={() =>
              save(async () => {
                const r = await importPayees(preview);
                setResult(
                  `Imported: ${r.inserted} added, ${r.updated} updated.`,
                );
                setPreview(null);
              })
            }
          >
            Import {preview.length} rows
          </Button>{" "}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setPreview(null)}
          >
            Discard
          </Button>
        </div>
      )}
      {result && <p className="text-sm text-green-700">{result}</p>}
      <div
        className="flex flex-wrap items-center gap-3 text-sm text-gray-600"
        data-testid="finance-payee-paging"
      >
        <span aria-live="polite">
          {listStatus()}
          {listQ.isFetching && !listQ.isLoading ? " (updating…)" : ""}
        </span>
        <Button
          size="sm"
          variant="secondary"
          disabled={page === 0 || listQ.isFetching}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          Previous
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={page >= lastPage || listQ.isFetching}
          onClick={() => setPage((p) => p + 1)}
          data-testid="finance-payee-next"
        >
          Next
        </Button>
      </div>
      <div className="max-h-96 overflow-y-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1">Name</th>
              <th>Type</th>
              <th>BC vendor no.</th>
              <th>Department</th>
              <th>Terms</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y">
            {shown.map((p) => (
              <tr key={p.id}>
                <td className="py-1.5">{p.display_name}</td>
                <td>{p.payee_type}</td>
                <td className="font-mono">{p.bc_vendor_no ?? "—"}</td>
                <td>
                  {lk.departments.find((d) => d.id === p.default_department_id)
                    ?.name ?? "—"}
                </td>
                <td>{p.payment_terms_code ?? "—"}</td>
                <td>
                  <Active on={p.is_active} />
                </td>
                <td className="text-right">
                  <button
                    type="button"
                    className="text-primary-600 hover:underline"
                    onClick={() => setF({ ...p })}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-end gap-2 border-t pt-3">
        <input
          aria-label="Payee name"
          placeholder="Name"
          className={inputCls}
          value={f.display_name}
          onChange={(e) => setF({ ...f, display_name: e.target.value })}
        />
        <input
          aria-label="BC vendor number"
          placeholder="BC vendor no."
          className={`${inputCls} w-32`}
          value={f.bc_vendor_no ?? ""}
          onChange={(e) => setF({ ...f, bc_vendor_no: e.target.value })}
        />
        <select
          aria-label="Payee department"
          className={inputCls}
          value={f.default_department_id ?? ""}
          onChange={(e) =>
            setF({ ...f, default_department_id: e.target.value || null })
          }
        >
          <option value="">Department…</option>
          {lk.departments.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
        <select
          aria-label="Payee payment terms"
          className={inputCls}
          value={f.payment_terms_code ?? ""}
          onChange={(e) =>
            setF({ ...f, payment_terms_code: e.target.value || null })
          }
        >
          <option value="">Terms…</option>
          {lk.terms.map((t) => (
            <option key={t.code} value={t.code}>
              {t.code}
            </option>
          ))}
        </select>
        <input
          aria-label="Payee state"
          placeholder="State"
          maxLength={2}
          className={`${inputCls} w-16`}
          value={f.state ?? ""}
          onChange={(e) => setF({ ...f, state: e.target.value.toUpperCase() })}
        />
        <label className="flex items-center gap-1 text-sm">
          <input
            type="checkbox"
            checked={f.is_active ?? true}
            onChange={(e) => setF({ ...f, is_active: e.target.checked })}
          />
          Active
        </label>
        <Button
          size="sm"
          disabled={busy || !f.display_name}
          onClick={() =>
            save(
              () => upsertPayee(f),
              () => setF(blank),
            )
          }
        >
          {f.id ? "Save payee" : "Add payee"}
        </Button>
        {f.id && (
          <Button size="sm" variant="secondary" onClick={() => setF(blank)}>
            New
          </Button>
        )}
      </div>
      <Err msg={error} />
    </Section>
  );
}

function TypesSection({ lk }: { lk: Lk }) {
  const { error, busy, save } = useSaver();
  const blank = { code: "", name: "", sort: "100", active: true };
  const [f, setF] = useState(blank);
  return (
    <Section
      title="Expense types"
      help="The list requesters choose from. Deactivate a type rather than renaming it."
    >
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500">
            <th className="py-1">Code</th>
            <th>Name</th>
            <th>Order</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody className="divide-y">
          {lk.types.map((t) => (
            <tr key={t.code}>
              <td className="py-1.5 font-mono">{t.code}</td>
              <td>{t.name}</td>
              <td>{t.sort_order}</td>
              <td>
                <Active on={t.is_active} />
              </td>
              <td className="text-right">
                <button
                  type="button"
                  className="text-primary-600 hover:underline"
                  onClick={() =>
                    setF({
                      code: t.code,
                      name: t.name,
                      sort: String(t.sort_order),
                      active: t.is_active,
                    })
                  }
                >
                  Edit
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex flex-wrap items-end gap-2 border-t pt-3">
        <input
          aria-label="Type code"
          placeholder="Code"
          className={`${inputCls} w-36`}
          value={f.code}
          onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })}
        />
        <input
          aria-label="Type name"
          placeholder="Name"
          className={inputCls}
          value={f.name}
          onChange={(e) => setF({ ...f, name: e.target.value })}
        />
        <input
          aria-label="Type order"
          type="number"
          className={`${inputCls} w-20`}
          value={f.sort}
          onChange={(e) => setF({ ...f, sort: e.target.value })}
        />
        <label className="flex items-center gap-1 text-sm">
          <input
            type="checkbox"
            checked={f.active}
            onChange={(e) => setF({ ...f, active: e.target.checked })}
          />
          Active
        </label>
        <Button
          size="sm"
          disabled={busy || !f.code || !f.name}
          onClick={() =>
            save(
              () =>
                upsertExpenseType({
                  code: f.code,
                  name: f.name,
                  is_active: f.active,
                  sort_order: Number(f.sort) || 100,
                }),
              () => setF(blank),
            )
          }
        >
          Save type
        </Button>
      </div>
      <Err msg={error} />
    </Section>
  );
}

function TermsSection({ lk }: { lk: Lk }) {
  const { error, busy, save } = useSaver();
  const blank = {
    code: "",
    name: "",
    days: "30",
    basis: "invoice_date" as PaymentReferenceBasis,
    active: true,
  };
  const [f, setF] = useState(blank);
  const basisLabel: Record<PaymentReferenceBasis, string> = {
    invoice_date: "Invoice date",
    service_date: "Service date",
    submission_date: "Submission date",
  };
  return (
    <Section
      title="Payment terms"
      help="How the expected payment date is worked out: a number of days after the invoice, service or submission date. Before an invoice exists, the service date stands in for the invoice date."
    >
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500">
            <th className="py-1">Code</th>
            <th>Name</th>
            <th>Days</th>
            <th>Counted from</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody className="divide-y">
          {lk.terms.map((t) => (
            <tr key={t.code}>
              <td className="py-1.5 font-mono">{t.code}</td>
              <td>{t.name}</td>
              <td>{t.net_days}</td>
              <td>{basisLabel[t.reference_basis]}</td>
              <td>
                <Active on={t.is_active} />
              </td>
              <td className="text-right">
                <button
                  type="button"
                  className="text-primary-600 hover:underline"
                  onClick={() =>
                    setF({
                      code: t.code,
                      name: t.name,
                      days: String(t.net_days),
                      basis: t.reference_basis,
                      active: t.is_active,
                    })
                  }
                >
                  Edit
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex flex-wrap items-end gap-2 border-t pt-3">
        <input
          aria-label="Terms code"
          placeholder="Code"
          className={`${inputCls} w-28`}
          value={f.code}
          onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })}
        />
        <input
          aria-label="Terms name"
          placeholder="Name"
          className={inputCls}
          value={f.name}
          onChange={(e) => setF({ ...f, name: e.target.value })}
        />
        <input
          aria-label="Net days"
          type="number"
          min="0"
          max="365"
          className={`${inputCls} w-20`}
          value={f.days}
          onChange={(e) => setF({ ...f, days: e.target.value })}
        />
        <select
          aria-label="Counted from"
          className={inputCls}
          value={f.basis}
          onChange={(e) =>
            setF({ ...f, basis: e.target.value as PaymentReferenceBasis })
          }
        >
          {Object.entries(basisLabel).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1 text-sm">
          <input
            type="checkbox"
            checked={f.active}
            onChange={(e) => setF({ ...f, active: e.target.checked })}
          />
          Active
        </label>
        <Button
          size="sm"
          disabled={busy || !f.code || !f.name}
          onClick={() =>
            save(
              () =>
                upsertPaymentTerm({
                  code: f.code,
                  name: f.name,
                  net_days: Number(f.days),
                  reference_basis: f.basis,
                  is_active: f.active,
                }),
              () => setF(blank),
            )
          }
        >
          Save terms
        </Button>
      </div>
      <Err msg={error} />
    </Section>
  );
}

function ManagersSection({
  people,
  peopleMap,
}: {
  people: StaffPerson[];
  peopleMap: Map<string, StaffPerson>;
}) {
  const { error, busy, save } = useSaver();
  const q = useQuery({
    queryKey: ["finance-managers"],
    queryFn: listApproverManagers,
  });
  const [who, setWho] = useState("");
  const [mgr, setMgr] = useState("");
  return (
    <Section
      title="Approver managers"
      help="Who each approver reports to, for finance purposes. Recorded now so escalation of overdue approvals can use it when it is switched on; nothing escalates yet."
    >
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500">
            <th className="py-1">Approver</th>
            <th>Manager</th>
            <th />
          </tr>
        </thead>
        <tbody className="divide-y">
          {(q.data ?? []).map((r) => (
            <tr key={r.profile_id}>
              <td className="py-1.5">{personName(peopleMap, r.profile_id)}</td>
              <td>{personName(peopleMap, r.manager_profile_id)}</td>
              <td className="text-right">
                <button
                  type="button"
                  className="text-red-600 hover:underline"
                  disabled={busy}
                  onClick={() =>
                    save(() => setApproverManager(r.profile_id, null))
                  }
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex flex-wrap items-end gap-2 border-t pt-3">
        <PersonSelect
          people={people}
          value={who}
          onChange={setWho}
          placeholder="Approver…"
        />
        <span className="text-sm text-gray-500">reports to</span>
        <PersonSelect
          people={people}
          value={mgr}
          onChange={setMgr}
          placeholder="Manager…"
        />
        <Button
          size="sm"
          disabled={busy || !who || !mgr}
          onClick={() =>
            save(
              () => setApproverManager(who, mgr),
              () => {
                setWho("");
                setMgr("");
              },
            )
          }
        >
          Save
        </Button>
      </div>
      <Err msg={error} />
    </Section>
  );
}

function AccessSection() {
  const { error, busy, save } = useSaver();
  const q = useQuery({
    queryKey: ["finance-grants"],
    queryFn: listCapabilityGrants,
  });
  return (
    <Section
      title="Access profiles"
      help="Finance access follows three profiles; nobody's CRM role changes. Admin: the CRM role “finance” and superadmins -- everything, settings, AP and audited overrides. Approver: anyone named as a department's approver or alternate, or as an approver's manager -- only the expenses in their own approval line. Field: anyone associated with an event (its creator, organizer, MSR, Sales Reps list, or a Team member with edit access) -- only that event's expenses."
    >
      {(q.data ?? []).length > 0 && (
        <>
          <p className="text-sm text-gray-600">
            Older individual grants below no longer give access; remove them to
            tidy up.
          </p>
          <ul className="divide-y text-sm">
            {(q.data ?? []).map((g) => (
              <li
                key={`${g.profile_id}-${g.capability}`}
                className="flex justify-between py-1.5"
              >
                <span>
                  {g.full_name || g.email} ({g.role}) — {g.capability}
                </span>
                <button
                  type="button"
                  className="text-red-600 hover:underline"
                  disabled={busy}
                  onClick={() =>
                    save(() => setCapability(g.profile_id, g.capability, false))
                  }
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      <Err msg={error} />
    </Section>
  );
}

function ApproversEscalationSection({
  lk,
  people,
}: {
  lk: Lk;
  people: StaffPerson[];
}) {
  const { error, busy, save } = useSaver();
  const [alt, setAlt] = useState<Record<string, string>>({});
  const [days, setDays] = useState<Record<string, string>>({});
  const name = (id: string | null | undefined) => {
    const p = people.find((x) => x.id === id);
    return p ? p.full_name || p.email : "Not set";
  };
  return (
    <>
      <Section
        title="Alternate approvers"
        help="Each department can have a second approver. Either the default approver or the alternate may decide; nobody can approve their own expense (an approver's own expense goes to the other one)."
      >
        <table className="min-w-full text-sm" data-testid="finance-alternates">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1">Department</th>
              <th>Default approver</th>
              <th>Alternate</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y">
            {lk.departments.map((d) => (
              <tr key={d.id}>
                <td className="py-1.5">{d.name}</td>
                <td>{name(d.default_approver_profile_id)}</td>
                <td>
                  <PersonSelect
                    people={people}
                    value={alt[d.id] ?? d.alternate_approver_profile_id ?? ""}
                    onChange={(v) => setAlt({ ...alt, [d.id]: v })}
                    placeholder="None"
                  />
                </td>
                <td className="text-right">
                  <Button
                    size="sm"
                    disabled={busy || alt[d.id] === undefined}
                    onClick={() =>
                      save(() =>
                        setDepartmentAlternate(d.id, alt[d.id] || null),
                      )
                    }
                  >
                    Save
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
      <Section
        title="Escalation by payment terms"
        help="How many business days an approval may wait before it goes to the approver's manager (and again before it goes to the finance-admin queue). Empty = the default in Rules."
      >
        <table
          className="min-w-full text-sm"
          data-testid="finance-term-escalation"
        >
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1">Terms</th>
              <th>Name</th>
              <th>Escalate after (business days)</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y">
            {lk.terms.map((t) => (
              <tr key={t.code}>
                <td className="py-1.5 font-mono">{t.code}</td>
                <td>{t.name}</td>
                <td>
                  <input
                    aria-label={`Escalation days for ${t.code}`}
                    type="number"
                    min="1"
                    max="30"
                    className={`${inputCls} w-24`}
                    value={
                      days[t.code] ??
                      (t.escalate_after_business_days !== null &&
                      t.escalate_after_business_days !== undefined
                        ? String(t.escalate_after_business_days)
                        : "")
                    }
                    placeholder="default"
                    onChange={(e) =>
                      setDays({ ...days, [t.code]: e.target.value })
                    }
                  />
                </td>
                <td className="text-right">
                  <Button
                    size="sm"
                    disabled={busy || days[t.code] === undefined}
                    onClick={() =>
                      save(() =>
                        setTermEscalation(
                          t.code,
                          days[t.code] ? Number(days[t.code]) : null,
                        ),
                      )
                    }
                  >
                    Save
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
      <Err msg={error} />
    </>
  );
}

export function BcExportSection({ lk }: { lk: Lk }) {
  const { error, busy, save } = useSaver();
  const entQ = useQuery({
    queryKey: ["finance-state-entities"],
    queryFn: listStateEntities,
  });
  const tplQ = useQuery({
    queryKey: ["finance-templates"],
    queryFn: listExportTemplates,
  });
  const setQ = useQuery({
    queryKey: ["finance-settings"],
    queryFn: listSettings,
  });
  const [ent, setEnt] = useState({
    state_code: "",
    entity_label: "",
    is_active: true,
  });
  const [it, setIt] = useState<Record<string, string>>({});
  const [kv, setKv] = useState<Record<string, string>>({});
  const [tplText, setTplText] = useState<string | null>(null);
  const active = (tplQ.data ?? []).find((t) => t.is_active);
  const setting = (k: string) => (setQ.data ?? []).find((s) => s.key === k);
  return (
    <>
      <Section
        title="State → BC entity"
        help="The STATE column of the import is the legal-entity label for the expense's state. An expense whose state has no entity cannot be exported."
      >
        <table
          className="min-w-full text-sm"
          data-testid="finance-state-entities"
        >
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1">State</th>
              <th>Entity label</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y">
            {(entQ.data ?? []).map((e) => (
              <tr key={e.state_code}>
                <td className="py-1.5 font-mono">{e.state_code}</td>
                <td>{e.entity_label}</td>
                <td>
                  <Active on={e.is_active} />
                </td>
                <td className="text-right">
                  <button
                    type="button"
                    className="text-primary-600 hover:underline"
                    onClick={() => setEnt(e)}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex flex-wrap items-end gap-2 border-t pt-3">
          <input
            aria-label="Entity state"
            placeholder="ST"
            maxLength={2}
            className={`${inputCls} w-16`}
            value={ent.state_code}
            onChange={(e) =>
              setEnt({ ...ent, state_code: e.target.value.toUpperCase() })
            }
          />
          <input
            aria-label="Entity label"
            placeholder="Entity label"
            className={`${inputCls} w-80`}
            value={ent.entity_label}
            onChange={(e) => setEnt({ ...ent, entity_label: e.target.value })}
          />
          <label className="flex items-center gap-1 text-sm">
            <input
              type="checkbox"
              checked={ent.is_active}
              onChange={(e) => setEnt({ ...ent, is_active: e.target.checked })}
            />
            Active
          </label>
          <Button
            size="sm"
            disabled={busy || ent.state_code.length !== 2 || !ent.entity_label}
            onClick={() =>
              save(
                () => upsertStateEntity(ent),
                () =>
                  setEnt({ state_code: "", entity_label: "", is_active: true }),
              )
            }
          >
            Save entity
          </Button>
        </div>
      </Section>
      <Section
        title="Invoice type per expense type"
        help="The INVOICE TYPE column (e.g. SE-001 Sponsorship Event). Required for export."
      >
        <table
          className="min-w-full text-sm"
          data-testid="finance-invoice-types"
        >
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1">Expense type</th>
              <th>BC invoice type</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y">
            {lk.types.map((t) => (
              <tr key={t.code}>
                <td className="py-1.5">{t.name}</td>
                <td>
                  <input
                    aria-label={`Invoice type for ${t.code}`}
                    className={`${inputCls} w-32`}
                    value={it[t.code] ?? t.bc_invoice_type ?? ""}
                    onChange={(e) =>
                      setIt({ ...it, [t.code]: e.target.value.toUpperCase() })
                    }
                  />
                </td>
                <td className="text-right">
                  <Button
                    size="sm"
                    disabled={busy || it[t.code] === undefined}
                    onClick={() =>
                      save(() =>
                        setExpenseTypeInvoiceType(t.code, it[t.code] || null),
                      )
                    }
                  >
                    Save
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-gray-500">
          Department codes (SLS, TED, …) are the departments' “BC dimension” in
          Departments & approvers.
        </p>
      </Section>
      <Section title="Line defaults" help="Values written on every line.">
        {(["export_territory", "export_activity"] as const).map((k) => (
          <div key={k} className="flex items-end gap-2">
            <label className="text-sm text-gray-700 w-32" htmlFor={k}>
              {k === "export_territory" ? "TERRITORY" : "ACTIVITY"}
            </label>
            <input
              id={k}
              className={`${inputCls} w-40`}
              value={kv[k] ?? String(setting(k)?.value ?? "")}
              onChange={(e) => setKv({ ...kv, [k]: e.target.value })}
            />
            <Button
              size="sm"
              disabled={busy || kv[k] === undefined}
              onClick={() => save(() => setSetting(k, kv[k]))}
            >
              Save
            </Button>
          </div>
        ))}
        <div className="flex items-end gap-2">
          <label className="text-sm text-gray-700 w-32" htmlFor="post-date">
            POST DATE
          </label>
          <select
            id="post-date"
            className={inputCls}
            value={
              kv.export_post_date_source ??
              String(setting("export_post_date_source")?.value ?? "export_date")
            }
            onChange={(e) =>
              setKv({ ...kv, export_post_date_source: e.target.value })
            }
          >
            <option value="export_date">Date the batch is created</option>
            <option value="invoice_date">Invoice date</option>
          </select>
          <Button
            size="sm"
            disabled={busy || kv.export_post_date_source === undefined}
            onClick={() =>
              save(() =>
                setSetting(
                  "export_post_date_source",
                  kv.export_post_date_source,
                ),
              )
            }
          >
            Save
          </Button>
        </div>
      </Section>
      <BcInvoiceNumbers
        prefix={String(setting("export_document_prefix")?.value ?? "")}
        pad={Number(setting("export_document_pad")?.value ?? 6)}
        busy={busy}
        save={save}
      />
      <Section
        title="Column layout"
        help="The import columns in order. A change saves a new version; earlier batches keep the version they used."
      >
        {active && (
          <div className="text-sm" data-testid="finance-template-active">
            <p className="font-medium">
              v{active.version}: {active.name}
              {active.is_placeholder ? " (placeholder)" : ""}
            </p>
            <p className="text-xs text-gray-500">
              Header row: {active.include_header ? "yes" : "no"} · dates{" "}
              {active.date_format} · amounts {active.amount_format} ·{" "}
              {active.line_ending.toUpperCase()}
            </p>
            <ol className="mt-2 list-decimal pl-6 text-xs text-gray-700">
              {active.columns.map((c, i) => (
                <li key={i}>
                  <span className="font-mono">{c.header}</span> ←{" "}
                  {c.source ??
                    (c.constant === "" ? "(blank)" : `“${c.constant}”`)}
                </li>
              ))}
            </ol>
            {tplText === null ? (
              <Button
                size="sm"
                variant="secondary"
                className="mt-2"
                onClick={() =>
                  setTplText(JSON.stringify(active.columns, null, 2))
                }
              >
                Edit columns
              </Button>
            ) : (
              <div className="mt-2 space-y-2">
                <textarea
                  aria-label="Template columns (JSON)"
                  rows={12}
                  className={`${inputCls} w-full font-mono text-xs`}
                  value={tplText}
                  onChange={(e) => setTplText(e.target.value)}
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      save(
                        () => {
                          const cols = JSON.parse(tplText);
                          return saveExportTemplate({
                            name: `${active.name.replace(/ \(v\d+ edit\)$/, "")} (v${active.version + 1} edit)`,
                            columns: cols,
                            delimiter: active.delimiter,
                            include_header: active.include_header,
                            date_format: active.date_format,
                            line_ending: active.line_ending,
                            amount_format: active.amount_format,
                          });
                        },
                        () => setTplText(null),
                      )
                    }
                  >
                    Save as new version
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setTplText(null)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Section>
      <Err msg={error} />
    </>
  );
}

/** CRM invoice number series for the INVOICE NUMBER column (migration 982) */
function BcInvoiceNumbers({
  prefix,
  pad,
  busy,
  save,
}: {
  prefix: string;
  pad: number;
  busy: boolean;
  save: (fn: () => Promise<unknown>, after?: () => void) => Promise<void>;
}) {
  const [p, setP] = useState<string | null>(null);
  const [d, setD] = useState<string | null>(null);
  const curP = p ?? prefix;
  const curD = d === null ? pad : Number(d);
  const err =
    p === null && d === null
      ? null
      : (validateBcPrefix(curP, curD) ?? validateBcPad(curD, curP));
  return (
    <Section
      title="Invoice numbers"
      help="Business Central needs a unique INVOICE NUMBER for every invoice. Each vendor invoice in an export gets the next CRM number; all of its lines carry it. The vendor's own invoice number goes into the line description. Numbers are never reused."
    >
      <div
        className="flex flex-wrap items-end gap-3"
        data-testid="finance-bc-invoice-numbers"
      >
        <label className="text-sm text-gray-700">
          Prefix
          <input
            aria-label="Invoice number prefix"
            className={`${inputCls} ml-2 w-32 font-mono`}
            maxLength={10}
            value={curP}
            onChange={(e) => setP(e.target.value.toUpperCase())}
          />
        </label>
        <label className="text-sm text-gray-700">
          Digits
          <input
            aria-label="Invoice number digits"
            type="number"
            min={4}
            max={12}
            className={`${inputCls} ml-2 w-20`}
            value={d ?? String(pad)}
            onChange={(e) => setD(e.target.value)}
          />
        </label>
        <Button
          size="sm"
          disabled={busy || (p === null && d === null) || !!err}
          onClick={() =>
            save(
              async () => {
                // shrink first, so the 20-character check never sees the larger combination half-saved
                const steps: [string, unknown][] = [];
                if (p !== null && p !== prefix) {
                  steps.push(["export_document_prefix", curP]);
                }
                if (d !== null && curD !== pad) {
                  steps.push(["export_document_pad", curD]);
                }
                if (curP.length > prefix.length) {
                  steps.reverse();
                }
                for (const [k, v] of steps) {
                  await setSetting(k, v);
                }
              },
              () => {
                setP(null);
                setD(null);
              },
            )
          }
        >
          Save
        </Button>
      </div>
      <p className="text-sm text-gray-700">
        Next invoices look like{" "}
        <span className="font-mono" data-testid="finance-bc-invoice-example">
          {formatBcDocumentNo(curP, Number.isInteger(curD) ? curD : pad, 1)}
        </span>
        , then{" "}
        {formatBcDocumentNo(curP, Number.isInteger(curD) ? curD : pad, 2)} …
        (gaps are normal: a cancelled export does not give its numbers back).
      </p>
      {err && (
        <p role="alert" className="text-sm text-red-700">
          {err}
        </p>
      )}
    </Section>
  );
}

const NUMBER_RULES: Record<string, string> = {
  escalation_business_days:
    "Default business days before an approval escalates",
  actual_auto_approve_pct:
    "Auto-approve an actual up to this % above the approved amount",
};

const RULE_LABEL: Record<string, string> = {
  escalation_enabled: "Escalate approvals that wait too long",
  escalation_original_can_decide:
    "After escalation the original approver may still decide",
  actual_requires_document:
    "An invoice or receipt must be attached before recording the actual",
  finance_role_is_finance_admin:
    "People with the CRM role “finance” are finance administrators",
  admins_have_event_expense_access:
    "Every CRM admin can see and add expenses on every event",
  allow_self_approval:
    "Allow people to approve expenses they created or submitted",
  notify_email_enabled:
    "Email approvers when an expense needs them, and requesters when it is decided",
};

export function RulesSection() {
  const { error, busy, save } = useSaver();
  const q = useQuery({ queryKey: ["finance-settings"], queryFn: listSettings });
  const rows = (q.data ?? []).filter((s) => s.key in RULE_LABEL);
  const nums = (q.data ?? []).filter((s) => s.key in NUMBER_RULES);
  const [numVal, setNumVal] = useState<Record<string, string>>({});
  return (
    <Section
      title="Rules"
      help="Rules marked “awaiting Finance” are recommended defaults until Finance confirms them. Changing one records who changed it."
    >
      <ul className="divide-y">
        {rows.map((s) => (
          <li
            key={s.key}
            className="flex items-start justify-between gap-4 py-3"
          >
            <div>
              <p className="text-sm font-medium text-gray-900">
                {RULE_LABEL[s.key]}
                {s.is_placeholder && (
                  <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                    awaiting Finance
                  </span>
                )}
              </p>
              <p className="text-xs text-gray-500">{s.description}</p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                aria-label={RULE_LABEL[s.key]}
                checked={s.value === true}
                disabled={busy}
                onChange={(e) =>
                  save(() => setSetting(s.key, e.target.checked))
                }
              />
              {s.value === true ? "On" : "Off"}
            </label>
          </li>
        ))}
        {nums.map((s) => (
          <li
            key={s.key}
            className="flex items-start justify-between gap-4 py-3"
          >
            <div>
              <p className="text-sm font-medium text-gray-900">
                {NUMBER_RULES[s.key]}
                {s.is_placeholder && (
                  <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                    awaiting Finance
                  </span>
                )}
              </p>
              <p className="text-xs text-gray-500">{s.description}</p>
            </div>
            <div className="flex items-center gap-2">
              <input
                aria-label={NUMBER_RULES[s.key]}
                type="number"
                className={`${inputCls} w-20`}
                value={numVal[s.key] ?? String(s.value)}
                onChange={(e) =>
                  setNumVal({ ...numVal, [s.key]: e.target.value })
                }
              />
              <Button
                size="sm"
                disabled={busy || numVal[s.key] === undefined}
                onClick={() =>
                  save(() => setSetting(s.key, Number(numVal[s.key])))
                }
              >
                Save
              </Button>
            </div>
          </li>
        ))}
      </ul>
      <Err msg={error} />
    </Section>
  );
}
